import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit, GripVertical, Monitor, Tablet, Smartphone } from "lucide-react";

function StrictModeDroppable(props) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props} />;
}

export default function BannerListPage() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBanners = async () => {
    try {
      const response = await api.get("/banner");
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.$values) ? raw.$values : [];
      const base = process.env.REACT_APP_UPLOAD_URL;
      const sorted = list
        .map((b, i) => {
          const desktop = b.imagePathPc || null;
          const tablet = b.imagePathTablet || null;
          const mobile = b.imagePathMobile || null;
          return {
            ...b,
            imageUrlDesktop: desktop ? `${base}/${desktop}` : null,
            imageUrlTablet: tablet ? `${base}/${tablet}` : null,
            imageUrlMobile: mobile ? `${base}/${mobile}` : null,
            sortOrder: b.sortOrder || i + 1,
          };
        })
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setBanners(sorted);
    } catch (err) {
      console.error("Banner fetch error:", err);
      toast.error("Banner'lar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleDelete = (id) => {
    if (!window.confirm("Bu banner'ı silmek istediğinize emin misiniz?"))
      return;
    api
      .delete(`/banner/${id}`)
      .then(() => {
        toast.success("Banner silindi");
        fetchBanners();
      })
      .catch(() => toast.error("Silme işlemi başarısız"));
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const items = Array.from(banners);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setBanners(items);

    try {
      await Promise.all(
        items.map((b, i) => {
          const fd = new FormData();
          fd.append("Name", b.name);
          fd.append("IsActive", b.isActive);
          fd.append("SortOrder", i + 1);
          return api.put(`/banner/${b.bannerId}`, fd);
        })
      );
      toast.success("Sıralama güncellendi");
    } catch {
      toast.error("Sıralama güncellenemedi");
      fetchBanners();
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Banner'lar</h2>
          <p className="page-subtitle">{banners.length} banner listeleniyor</p>
        </div>
        <Link to="/add-banner" className="btn btn-primary">
          <Plus size={18} /> Yeni Banner
        </Link>
      </div>

      <div className="content-card no-padding">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Desktop</th>
                  <th>Tablet</th>
                  <th>Mobil</th>
                  <th>Ad</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <StrictModeDroppable droppableId="banners">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {banners.map((banner, index) => (
                      <Draggable
                        key={String(banner.bannerId)}
                        draggableId={String(banner.bannerId)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "dragging" : ""}
                          >
                            <td
                              {...provided.dragHandleProps}
                              className="drag-handle"
                            >
                              <GripVertical size={18} />
                            </td>
                            <td>
                              {banner.imageUrlDesktop ? (
                                <img src={banner.imageUrlDesktop} alt="Desktop" className="table-thumb" />
                              ) : (
                                <div className="table-thumb-placeholder"><Monitor size={16} /></div>
                              )}
                            </td>
                            <td>
                              {banner.imageUrlTablet ? (
                                <img src={banner.imageUrlTablet} alt="Tablet" className="table-thumb" />
                              ) : (
                                <div className="table-thumb-placeholder"><Tablet size={16} /></div>
                              )}
                            </td>
                            <td>
                              {banner.imageUrlMobile ? (
                                <img src={banner.imageUrlMobile} alt="Mobil" className="table-thumb" />
                              ) : (
                                <div className="table-thumb-placeholder"><Smartphone size={16} /></div>
                              )}
                            </td>
                            <td className="fw-medium">{banner.name}</td>
                            <td>
                              <span
                                className={`status-badge ${banner.isActive ? "active" : "inactive"}`}
                              >
                                {banner.isActive ? "Aktif" : "Pasif"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/edit-banner/${banner.bannerId}`}
                                  className="btn-icon edit"
                                  title="Düzenle"
                                >
                                  <Edit size={16} />
                                </Link>
                                <button
                                  onClick={() => handleDelete(banner.bannerId)}
                                  className="btn-icon delete"
                                  title="Sil"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </StrictModeDroppable>
            </table>
          </div>
        </DragDropContext>

        {banners.length === 0 && (
          <div className="empty-state">
            <p>Henüz banner eklenmemiş</p>
            <Link to="/add-banner" className="btn btn-primary">
              <Plus size={18} /> İlk Banner'ı Ekle
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
